import localforage from 'localforage';

// Configure localforage
localforage.config({
  name: 'FreelaDash',
  storeName: 'offline_data',
  description: 'FreelaDash offline storage',
});

// Offline Time Entry interface
export interface OfflineTimeEntry {
  id: string;
  local_id: string;
  synced: boolean;
  created_at_local: string;
  user_id: string;
  project_id: string | null;
  category: string;
  custom_category?: string | null;
  description?: string | null;
  start_time: string;
  end_time?: string | null;
  duration_minutes?: number | null;
  is_billable?: boolean;
  hourly_rate?: number | null;
  is_paused?: boolean;
  paused_duration_seconds?: number | null;
}

// Active timer state for offline persistence
export interface OfflineTimerState {
  isActive: boolean;
  startTime: string | null;
  projectId: string | null;
  category: string;
  description: string;
  hourlyRate: number | null;
  isBillable: boolean;
  isPaused: boolean;
  pausedAt: number | null;
  totalPausedSeconds: number;
}

const OFFLINE_ENTRIES_KEY = 'offline_time_entries';
const TIMER_STATE_KEY = 'offline_timer_state';
const CACHED_DATA_KEY = 'cached_dashboard_data';

// Generate a unique local ID
function generateLocalId(): string {
  return `local_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

// Offline Time Entries Storage
export const offlineStorage = {
  // Get all offline entries
  async getOfflineEntries(): Promise<OfflineTimeEntry[]> {
    const entries = await localforage.getItem<OfflineTimeEntry[]>(OFFLINE_ENTRIES_KEY);
    return entries || [];
  },

  // Add a new offline entry
  async addOfflineEntry(entry: Omit<OfflineTimeEntry, 'local_id' | 'synced' | 'created_at_local'>): Promise<OfflineTimeEntry> {
    const entries = await this.getOfflineEntries();
    const newEntry: OfflineTimeEntry = {
      ...entry,
      local_id: generateLocalId(),
      synced: false,
      created_at_local: new Date().toISOString(),
    };
    entries.push(newEntry);
    await localforage.setItem(OFFLINE_ENTRIES_KEY, entries);
    return newEntry;
  },

  // Update an offline entry
  async updateOfflineEntry(localId: string, updates: Partial<OfflineTimeEntry>): Promise<void> {
    const entries = await this.getOfflineEntries();
    const index = entries.findIndex(e => e.local_id === localId);
    if (index !== -1) {
      entries[index] = { ...entries[index], ...updates };
      await localforage.setItem(OFFLINE_ENTRIES_KEY, entries);
    }
  },

  // Get unsynced entries
  async getUnsyncedEntries(): Promise<OfflineTimeEntry[]> {
    const entries = await this.getOfflineEntries();
    return entries.filter(e => !e.synced);
  },

  // Mark entries as synced
  async markAsSynced(localIds: string[]): Promise<void> {
    const entries = await this.getOfflineEntries();
    const updatedEntries = entries.map(e => 
      localIds.includes(e.local_id) ? { ...e, synced: true } : e
    );
    await localforage.setItem(OFFLINE_ENTRIES_KEY, updatedEntries);
  },

  // Remove synced entries (cleanup)
  async cleanupSyncedEntries(): Promise<void> {
    const entries = await this.getOfflineEntries();
    const unsyncedEntries = entries.filter(e => !e.synced);
    await localforage.setItem(OFFLINE_ENTRIES_KEY, unsyncedEntries);
  },

  // Get pending count
  async getPendingCount(): Promise<number> {
    const entries = await this.getUnsyncedEntries();
    return entries.length;
  },
};

// Timer State Storage
export const timerStateStorage = {
  async getTimerState(): Promise<OfflineTimerState | null> {
    return localforage.getItem<OfflineTimerState>(TIMER_STATE_KEY);
  },

  async saveTimerState(state: OfflineTimerState): Promise<void> {
    await localforage.setItem(TIMER_STATE_KEY, state);
  },

  async clearTimerState(): Promise<void> {
    await localforage.removeItem(TIMER_STATE_KEY);
  },
};

// Cached Dashboard Data
export interface CachedDashboardData {
  projects: any[];
  clients: any[];
  recentEntries: any[];
  lastUpdated: string;
}

export const cachedDataStorage = {
  async getCachedData(): Promise<CachedDashboardData | null> {
    return localforage.getItem<CachedDashboardData>(CACHED_DATA_KEY);
  },

  async setCachedData(data: CachedDashboardData): Promise<void> {
    await localforage.setItem(CACHED_DATA_KEY, data);
  },

  async clearCachedData(): Promise<void> {
    await localforage.removeItem(CACHED_DATA_KEY);
  },
};
